/**
 * The rider's app context (Message.client_context) as the panel's rows.
 *
 * ant stores what the app sent, validated only as a map of at most 8 KB, so
 * every field is optional and read defensively: snake_case as the spec
 * writes it, camelCase accepted too, numbers turned into text, anything
 * else ignored.
 *
 * Shape (all optional): {app_version, build, runtime_version, update_id,
 * os, os_version, device_model, locale, network ("wifi"|"cellular"|"none"),
 * location_permission, notifications_permission,
 * ble: {state, last_error, last_error_at, last_hardware_id},
 * rental: {id, lock_state, hardware_type}}
 */

import { formatAgo, formatDateTime } from './format';

export interface ContextRow {
  label: string;
  value: string;
  /** Worth a look: a permission denied, Bluetooth off, the network down, a BLE error. */
  tone?: 'warning' | 'error';
  /** The full value, for the tooltip. */
  detail?: string;
}

type Obj = Record<string, unknown>;

const camel = (key: string) =>
  key.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

/** A text field by its snake_case key or its camelCase twin; '' when absent. */
function text(obj: Obj | null | undefined, key: string): string {
  if (!obj) return '';
  const value = obj[key] ?? obj[camel(key)];
  if (typeof value === 'string') return value.trim();
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function child(obj: Obj, key: string): Obj | null {
  const value = obj[key] ?? obj[camel(key)];
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Obj)
    : null;
}

/** "powered_off" → "Powered off", "poweredOn" → "Powered on". */
export function humanize(value: string): string {
  const spaced = value
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .trim()
    .toLowerCase();
  return spaced ? spaced[0].toUpperCase() + spaced.slice(1) : '';
}

const OS_NAMES: Record<string, string> = { ios: 'iOS', android: 'Android', web: 'Web' };

const NETWORK: Record<string, { value: string; tone?: ContextRow['tone'] }> = {
  wifi: { value: 'Wi-Fi' },
  cellular: { value: 'Cellular' },
  none: { value: 'Offline', tone: 'error' },
};

/** A permission status: anything but granted deserves a look. */
function permission(status: string): { value: string; tone?: ContextRow['tone'] } {
  switch (status.toLowerCase()) {
    case 'granted':
      return { value: 'Granted' };
    case 'always':
      return { value: 'Always' };
    case 'wheninuse':
    case 'when_in_use':
      return { value: 'While using the app' };
    case 'undetermined':
    case 'not_determined':
      return { value: 'Not asked yet', tone: 'warning' };
    default:
      return { value: humanize(status), tone: 'warning' };
  }
}

/** Bluetooth's state: on is fine, anything else explains a failed unlock. */
function bluetooth(state: string): { value: string; tone?: ContextRow['tone'] } {
  const key = state.toLowerCase().replace(/[_\s-]/g, '');
  if (key === 'on' || key === 'poweredon') return { value: 'On' };
  if (key === 'off' || key === 'poweredoff') return { value: 'Off', tone: 'warning' };
  if (key === 'unauthorized') return { value: 'Not allowed', tone: 'warning' };
  if (key === 'unsupported') return { value: 'Not supported', tone: 'warning' };
  return { value: humanize(state), tone: 'warning' };
}

/** Rows for the panel, in reading order; empty when the context says nothing known. */
export function describeClientContext(
  ctx: Obj,
  nowMs: number = Date.now()
): ContextRow[] {
  const rows: ContextRow[] = [];
  const add = (row: ContextRow | null) => {
    if (row && row.value) rows.push(row);
  };

  const osKey = text(ctx, 'os');
  const os = [OS_NAMES[osKey.toLowerCase()] ?? osKey, text(ctx, 'os_version')]
    .filter(Boolean)
    .join(' ');
  add({
    label: 'Device',
    value: [text(ctx, 'device_model'), os].filter(Boolean).join(' · '),
  });

  const appVersion = text(ctx, 'app_version');
  const build = text(ctx, 'build');
  add({
    label: 'App',
    value: [appVersion, build && `(build ${build})`].filter(Boolean).join(' '),
  });

  const runtime = text(ctx, 'runtime_version');
  const updateId = text(ctx, 'update_id');
  add({
    label: 'Update',
    value: [
      runtime && `runtime ${runtime}`,
      updateId ? `update ${updateId.slice(0, 8)}` : runtime && 'built-in bundle',
    ]
      .filter(Boolean)
      .join(' · '),
    detail: updateId || undefined,
  });

  add({ label: 'Language', value: text(ctx, 'locale') });

  const network = text(ctx, 'network');
  if (network) {
    const known = NETWORK[network.toLowerCase()];
    add({
      label: 'Network',
      value: known?.value ?? humanize(network),
      tone: known?.tone,
    });
  }

  const location = text(ctx, 'location_permission');
  if (location) add({ label: 'Location', ...permission(location) });
  const notifications = text(ctx, 'notifications_permission');
  if (notifications) add({ label: 'Notifications', ...permission(notifications) });

  const ble = child(ctx, 'ble');
  const bleState = text(ble, 'state');
  if (bleState) add({ label: 'Bluetooth', ...bluetooth(bleState) });
  const bleError = text(ble, 'last_error');
  if (bleError) {
    const at = text(ble, 'last_error_at');
    const ago = at ? formatAgo(at, nowMs) : '';
    const hardware = text(ble, 'last_hardware_id');
    add({
      label: 'Last BLE error',
      value: [bleError, ago && (ago === 'just now' ? ago : `${ago} ago`)]
        .filter(Boolean)
        .join(' · '),
      tone: 'error',
      detail:
        [at && formatDateTime(at), hardware && `hardware ${hardware}`]
          .filter(Boolean)
          .join(' · ') || undefined,
    });
  }

  const rental = child(ctx, 'rental');
  const lockState = text(rental, 'lock_state');
  if (lockState) {
    const hardwareType = text(rental, 'hardware_type');
    add({
      label: 'Lock',
      value: [humanize(lockState), hardwareType && humanize(hardwareType)]
        .filter(Boolean)
        .join(' · '),
      detail: text(rental, 'id') ? `rental ${text(rental, 'id')}` : undefined,
    });
  }

  return rows;
}
